import { useEffect, useMemo, useRef, useState } from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import { useApi } from '@/hooks'
import { buildQueryWithArrays } from '@/insights/common/queryUtils'
import { InsightParametersType } from '@/types/dashboard-types'

interface Geofence {
  id: string
  name: string
  companyId: string
  coordinates: [number, number][]
  zOrder?: number
  geofenceCategoryId?: number
}

// Calculate metersPerPixel for Mapbox
const getMetersPerPixel = (zoom: number, latitude: number): number => {
  return (40075017 * Math.cos((latitude * Math.PI) / 180)) / (256 * Math.pow(2, zoom))
}

// Color mapping for zOrders
const getZOrderColor = (zOrder: number): string => {
  const colors: Record<number, string> = {
    1: '#3b82f6', // blue
    2: '#22c55e', // green
    3: '#eab308', // yellow
    4: '#f97316', // orange
    5: '#ef4444', // red
  }
  return colors[zOrder] || '#3b82f6'
}

const ZORDER_COLOR = [
  'case',
  ['==', ['get', 'zOrder'], 1],
  getZOrderColor(1),
  ['==', ['get', 'zOrder'], 2],
  getZOrderColor(2),
  ['==', ['get', 'zOrder'], 3],
  getZOrderColor(3),
  ['==', ['get', 'zOrder'], 4],
  getZOrderColor(4),
  ['==', ['get', 'zOrder'], 5],
  getZOrderColor(5),
  getZOrderColor(1), // default
] as unknown as mapboxgl.ExpressionSpecification

export interface GeofencesInViewProps {
  companyId: number | string | undefined
  zOrders?: number[]
  categoryIds?: number[]
  /** Show nothing until some zOrders or categories are picked (the asset map's picker). */
  requireFilter?: boolean
  visible?: boolean
  /** Draw the geofences under this layer, when the map has it. */
  beforeId?: string
}

/**
 * The company's geofences within the map's viewport, filled and outlined in their zOrder's
 * colour, refetched (debounced) as the map pans and zooms. Render it inside a react-map-gl
 * map. Shared by the asset map and the route history map.
 */
export const GeofencesInView = ({
  companyId,
  zOrders = [],
  categoryIds = [],
  requireFilter = false,
  visible = true,
  beforeId,
}: GeofencesInViewProps) => {
  const { api } = useApi()
  const { current: map } = useMap()
  const [geofences, setGeofences] = useState<Geofence[]>([])
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const zOrderKey = zOrders.join(',')
  const categoryKey = categoryIds.join(',')

  useEffect(() => {
    if (!map || !companyId || !api || !visible || (requireFilter && !zOrders.length && !categoryIds.length)) {
      setGeofences([])
      return
    }

    const fetchGeofences = async () => {
      try {
        const bounds = map.getBounds()
        if (!bounds) {
          setGeofences([])
          return
        }
        const center = bounds.getCenter()
        const params: InsightParametersType = {
          companyId,
          east: bounds.getEast(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          metersPerPixel: getMetersPerPixel(map.getZoom(), center.lat),
        }
        // API expects arrays for multiple values
        if (zOrders.length > 0) {
          params.zOrders = zOrders
        }
        if (categoryIds.length > 0) {
          params.geofenceCategoryIds = categoryIds
        }
        const path = `/fresh/geofences/by-map-boundaries?${buildQueryWithArrays(params)}`
        const response = await api.get<Geofence[]>({ path, origin: 'GeofencesInView' })
        setGeofences(response?.data ?? [])
      } catch (error) {
        console.error('Error fetching geofences:', error)
        setGeofences([])
      }
    }

    void fetchGeofences()

    const handleMove = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      debounceTimeoutRef.current = setTimeout(() => void fetchGeofences(), 300)
    }
    map.on('moveend', handleMove)
    map.on('zoomend', handleMove)

    return () => {
      map.off('moveend', handleMove)
      map.off('zoomend', handleMove)
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
    // The filters are compared by value (their keys), not by array identity.
  }, [companyId, map, api, visible, requireFilter, zOrderKey, categoryKey])

  // Convert geofences to GeoJSON (already filtered by API)
  const geofenceGeoJSON = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: geofences.map((geofence) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [geofence.coordinates.map((coord) => [coord[1], coord[0]])], // Convert [lat, lng] to [lng, lat]
        },
        properties: {
          id: geofence.id,
          name: geofence.name,
          zOrder: geofence.zOrder || 1,
        },
      })),
    }),
    [geofences]
  )

  if (!visible || geofenceGeoJSON.features.length === 0) return null
  const before = beforeId && map?.getMap().getLayer(beforeId) ? beforeId : undefined

  return (
    <Source
      key={`geofences-${String(companyId)}-${zOrderKey}-${categoryKey}`}
      id="geofences-source"
      type="geojson"
      data={geofenceGeoJSON}
    >
      <Layer
        id="geofence-fill"
        type="fill"
        beforeId={before}
        paint={{ 'fill-color': ZORDER_COLOR, 'fill-opacity': 0.1 }}
      />
      <Layer
        id="geofence-stroke"
        type="line"
        beforeId={before}
        paint={{ 'line-color': ZORDER_COLOR, 'line-width': 2 }}
      />
    </Source>
  )
}
